import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Persists unsaved edits to localStorage every ~10 seconds so an accidental
 * tab-close / refresh never loses work.
 *
 * Behaviour:
 *  - Once the caller flips `enabled` to true (usually after the initial server
 *    fetch), we start snapshotting `data` to localStorage every `interval` ms
 *    IF it differs from `savedRef.current` (the last-saved-to-server state).
 *  - On mount we look for an existing draft under `storageKey`. If it exists
 *    AND was saved AFTER `savedAt`, we return `hasDraft: true` so the caller
 *    can prompt "Restore unsaved changes?".
 *  - Call `markSaved(currentData)` right after a successful server save — this
 *    updates the reference and clears any stored draft.
 *  - Call `discardDraft()` to explicitly drop the stored draft.
 *
 * Args:
 *   storageKey: string, unique per manager (e.g. "draft:about_main")
 *   data:       the live editable state
 *   enabled:    only start persisting once data is loaded from server
 *   savedAt:    ISO timestamp of the last server-saved version
 *   interval:   ms between draft writes (default 10 s)
 */
export function useAutoSaveDraft({ storageKey, data, enabled, savedAt, interval = 10_000 }) {
  const savedRef = useRef(null);   // last-saved-to-server snapshot
  const dataRef = useRef(data);
  dataRef.current = data;

  const [hasDraft, setHasDraft] = useState(false);
  const [draftAt, setDraftAt] = useState(null);

  // Check for existing draft on mount / when enabled flips
  useEffect(() => {
    if (!enabled) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const draftTime = new Date(parsed.savedAt || 0).getTime();
      const serverTime = new Date(savedAt || 0).getTime();
      if (parsed.data && draftTime > serverTime) {
        setHasDraft(true);
        setDraftAt(parsed.savedAt);
      } else {
        // Stale draft (server is newer) — clear it silently
        localStorage.removeItem(storageKey);
      }
    } catch {
      /* ignore */
    }
  }, [enabled, storageKey, savedAt]);

  // Periodic writer
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => {
      try {
        const current = dataRef.current;
        // Only persist if changed vs. the last server-saved snapshot
        if (savedRef.current && JSON.stringify(savedRef.current) === JSON.stringify(current)) return;
        localStorage.setItem(
          storageKey,
          JSON.stringify({ savedAt: new Date().toISOString(), data: current }),
        );
      } catch {
        /* quota exceeded etc — safe to ignore */
      }
    }, interval);
    return () => clearInterval(id);
  }, [enabled, storageKey, interval]);

  const markSaved = useCallback((current) => {
    savedRef.current = current;
    try { localStorage.removeItem(storageKey); } catch { /* noop */ }
    setHasDraft(false);
    setDraftAt(null);
  }, [storageKey]);

  const readDraft = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.data ?? null;
    } catch {
      return null;
    }
  }, [storageKey]);

  const discardDraft = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch { /* noop */ }
    setHasDraft(false);
    setDraftAt(null);
  }, [storageKey]);

  return { hasDraft, draftAt, readDraft, discardDraft, markSaved };
}

export default useAutoSaveDraft;
